import { Alert, AlertTitle, capitalize, Snackbar } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { DashboardGrid } from '~/components/organisms/DashboardGrid';
import { ReportsBanner } from '~/components/organisms/ReportsBanner';
import { useSnackbar } from '~/hooks/useSnackbar';
import { useQuickSightEmbedContext } from '~/providers/QuickSightEmbedProvider';

export const ReportsIndex: FC = () => {
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const { activeDashboardId, showDashboard, hideDashboard, error, clearError } = useQuickSightEmbedContext();

  const handleSelect = useCallback(
    (id: string | null) => {
      if (id) {
        showDashboard(id);
      } else {
        hideDashboard();
      }
    },
    [showDashboard, hideDashboard],
  );

  // Reset to grid view when navigating away from /reports
  useEffect(() => {
    return () => {
      hideDashboard();
    };
  }, [hideDashboard]);

  // Show snackbar when embed errors occur
  useEffect(() => {
    if (error) {
      showSnackbar(error.message, 'error');
      clearError();
    }
  }, [error, showSnackbar, clearError]);

  // QuickSight enabled: show banner + grid or report view
  return (
    <>
      <ReportsBanner selectedDashboardId={activeDashboardId} onSelect={handleSelect} />

      {!activeDashboardId && <DashboardGrid onSelect={handleSelect} />}

      <Snackbar
        open={snackbar.isOpen && snackbar.message !== ''}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.type && <AlertTitle>{capitalize(snackbar.type)}</AlertTitle>}
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
